/* =============================================================================
 * METRO — theme behaviour
 *
 * Every interaction the React app handled with component state is reimplemented
 * here as small vanilla controllers:
 *
 *   toasts          -> sonner
 *   header          -> SiteHeader (hide-on-scroll, dropdowns, mobile drawer)
 *   countdown       -> useCountdown() in routes/index.tsx
 *   tabs            -> useState toggles (model / finish / colour switchers)
 *   accordion       -> openFaq state
 *   quiz            -> quizStep / quizScore state machine
 *   builder         -> "Build your ride" model x colour matrix
 *   cart            -> stores/cartStore.ts + CartDrawer.tsx (AJAX cart API)
 *   variants        -> product colour chips -> Shopify variant selection
 *   sticky bar      -> showBar scroll state
 *   drag stage      -> pointer-drag rotate on the dark product template
 *   model stage     -> ModelStage.tsx lazy <model-viewer> loader
 *   contact form    -> zod-validated mailto handoff
 *
 * Document-level delegates are bound once; per-element controllers are marked
 * with data-metro-ready so theme-editor section reloads never double-bind.
 * ========================================================================== */
(function () {
  'use strict';

  var Theme = window.MetroTheme || {};
  var routes = Theme.routes || {};
  var strings = Theme.strings || {};

  /* ---------------------------------------------------------------- utils */

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  function on(type, selector, handler) {
    document.addEventListener(type, function (event) {
      var target = event.target.closest ? event.target.closest(selector) : null;
      if (target && document.contains(target)) handler(event, target);
    });
  }
  /** Runs `fn` once per element, even after a section is re-rendered. */
  function each(selector, key, fn) {
    $$(selector).forEach(function (el) {
      var flag = 'metroReady' + key;
      if (el.dataset[flag] === 'true') return;
      el.dataset[flag] = 'true';
      fn(el);
    });
  }
  function setClasses(el, list, add) {
    if (!list) return;
    list
      .split(/\s+/)
      .filter(Boolean)
      .forEach(function (cls) {
        el.classList[add ? 'add' : 'remove'](cls);
      });
  }
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }
  function isLink(el) {
    return el.tagName === 'A' && el.getAttribute('href');
  }

  /** Shopify money formatter (cents -> the shop's money format). */
  function formatMoney(cents, format) {
    var amount = Number(
      typeof cents === 'string' ? cents.replace(/[^0-9.-]/g, '') : cents
    );
    if (isNaN(amount)) return '';
    var fmt = format || Theme.moneyFormat || '${{amount}}';

    function withCommas(num, decimals) {
      var parts = (num / 100).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
      return parts.join('.');
    }

    var placeholder = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = placeholder ? placeholder[1] : 'amount';
    var out;
    switch (key) {
      case 'amount_no_decimals':
        out = withCommas(amount, 0);
        break;
      case 'amount_with_comma_separator':
        out = withCommas(amount, 2).replace(/,/g, ' ').replace('.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = withCommas(amount, 0).replace(/,/g, ' ').replace('.', ',');
        break;
      default:
        out = withCommas(amount, 2);
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
  }

  /* --------------------------------------------------------------- toasts */

  var Toast = {
    show: function (message, type, duration) {
      var host = document.getElementById('metro-toasts');
      if (!host || !message) return;
      var el = document.createElement('div');
      el.className = 'metro-toast';
      el.setAttribute('data-type', type || 'default');
      el.setAttribute('role', 'status');
      el.textContent = message;
      host.appendChild(el);
      setTimeout(function () {
        el.setAttribute('data-leaving', 'true');
        setTimeout(function () {
          el.remove();
        }, 220);
      }, duration || 3200);
    },
    success: function (m) {
      Toast.show(m, 'success');
    },
    error: function (m) {
      Toast.show(m, 'error');
    },
    info: function (m) {
      Toast.show(m, 'info');
    }
  };
  window.MetroToast = Toast;

  /* --------------------------------------------------------------- header */

  function initHeader() {
    each('[data-metro-header]', 'Header', function (header) {
      var lastY = window.scrollY;
      function onScroll() {
        var y = window.scrollY;
        header.setAttribute('data-scrolled', y > 20 ? 'true' : 'false');
        if (y > 80 && y > lastY + 4) header.classList.add('-translate-y-full');
        else if (y < lastY - 4) header.classList.remove('-translate-y-full');
        lastY = y;
      }
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    });

    // Desktop dropdowns: CSS handles hover, JS adds keyboard and touch support.
    each('[data-dropdown]', 'Dropdown', function (group) {
      var button = $('[data-dropdown-trigger]', group);
      var panel = $('[data-dropdown-panel]', group);
      if (!button || !panel) return;

      function open(state) {
        group.setAttribute('data-open', state ? 'true' : 'false');
        button.setAttribute('aria-expanded', state ? 'true' : 'false');
        panel.classList.toggle('invisible', !state);
        panel.classList.toggle('opacity-0', !state);
      }
      button.addEventListener('click', function (e) {
        e.preventDefault();
        open(group.getAttribute('data-open') !== 'true');
      });
      group.addEventListener('focusout', function (e) {
        if (!group.contains(e.relatedTarget)) open(false);
      });
      group.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          open(false);
          button.focus();
        }
      });
      document.addEventListener('click', function (e) {
        if (!group.contains(e.target)) open(false);
      });
    });
  }

  /* -------------------------------------------------- drawers (generic) */

  var lockCount = 0;
  function lockScroll(lock) {
    lockCount = Math.max(0, lockCount + (lock ? 1 : -1));
    document.documentElement.style.overflow = lockCount > 0 ? 'hidden' : '';
  }

  function openDrawer(id) {
    var drawer = document.querySelector('[data-drawer="' + id + '"]');
    if (!drawer || drawer.getAttribute('data-open') === 'true') return;
    drawer.hidden = false;
    drawer.setAttribute('data-open', 'true');
    requestAnimationFrame(function () {
      var overlay = $('.metro-overlay', drawer);
      var panel = $('.metro-panel', drawer);
      if (overlay) overlay.setAttribute('data-open', 'true');
      if (panel) panel.setAttribute('data-open', 'true');
    });
    lockScroll(true);
    var focusable = $('[data-drawer-autofocus], button, a[href]', drawer);
    if (focusable) focusable.focus({ preventScroll: true });
  }

  function closeDrawer(id) {
    var drawer = document.querySelector('[data-drawer="' + id + '"]');
    if (!drawer || drawer.getAttribute('data-open') !== 'true') return;
    var overlay = $('.metro-overlay', drawer);
    var panel = $('.metro-panel', drawer);
    if (overlay) overlay.setAttribute('data-open', 'false');
    if (panel) panel.setAttribute('data-open', 'false');
    drawer.setAttribute('data-open', 'false');
    lockScroll(false);
    setTimeout(function () {
      if (drawer.getAttribute('data-open') === 'false') drawer.hidden = true;
    }, 400);
  }
  window.MetroDrawer = { open: openDrawer, close: closeDrawer };

  function bindDrawers() {
    on('click', '[data-drawer-open]', function (e, el) {
      e.preventDefault();
      openDrawer(el.getAttribute('data-drawer-open'));
    });

    on('click', '[data-drawer-close]', function (e, el) {
      // Links inside a drawer must still navigate — close, then let it through.
      if (!isLink(el)) e.preventDefault();
      var id = el.getAttribute('data-drawer-close');
      if (!id) {
        var host = el.closest('[data-drawer]');
        id = host && host.getAttribute('data-drawer');
      }
      if (id) closeDrawer(id);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      $$('[data-drawer]').forEach(function (d) {
        if (!d.hidden) closeDrawer(d.getAttribute('data-drawer'));
      });
    });
  }

  /* ------------------------------------------------------------ countdown */

  function initCountdown() {
    var nodes = $$('[data-countdown]').filter(function (n) {
      if (n.dataset.metroReadyCountdown === 'true') return false;
      n.dataset.metroReadyCountdown = 'true';
      return true;
    });
    if (!nodes.length) return;

    function pad(n) {
      return String(n).padStart(2, '0');
    }

    // Same target the React hook computed: the coming Sunday at 23:59:59.
    function target() {
      var t = new Date();
      t.setDate(t.getDate() + ((7 - t.getDay()) % 7 || 7));
      t.setHours(23, 59, 59, 0);
      return t.getTime();
    }

    var end = target();
    function tick() {
      var diff = Math.max(0, end - Date.now());
      var text =
        pad(Math.floor(diff / 86400000)) +
        'D ' +
        pad(Math.floor((diff / 3600000) % 24)) +
        'H ' +
        pad(Math.floor((diff / 60000) % 60)) +
        'M ' +
        pad(Math.floor((diff / 1000) % 60)) +
        'S';
      nodes.forEach(function (n) {
        n.textContent = text;
      });
      if (diff === 0) end = target();
    }
    tick();
    setInterval(tick, 1000);
  }

  /* ----------------------------------------------------------------- tabs */
  /* <div data-metro-tabs data-active="city">
   *   <button data-tab="city" data-active-class="…" data-idle-class="…">
   *   <div data-panel="city">…</div>
   * </div>                                                                  */

  function applyTabs(container, value) {
    container.setAttribute('data-active', value);

    $$('[data-tab]', container).forEach(function (btn) {
      if (btn.closest('[data-metro-tabs]') !== container) return;
      var active = btn.getAttribute('data-tab') === value;
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (btn.hasAttribute('aria-selected')) {
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
      }
      setClasses(btn, btn.getAttribute('data-active-class'), active);
      setClasses(btn, btn.getAttribute('data-idle-class'), !active);
    });

    $$('[data-panel]', container).forEach(function (panel) {
      if (panel.closest('[data-metro-tabs]') !== container) return;
      panel.hidden = panel.getAttribute('data-panel') !== value;
    });

    container.dispatchEvent(
      new CustomEvent('metro:tabchange', { bubbles: true, detail: { value: value } })
    );
  }

  function initTabs() {
    $$('[data-metro-tabs]').forEach(function (container) {
      applyTabs(container, container.getAttribute('data-active') || '');
    });
  }

  function bindTabs() {
    on('click', '[data-tab]', function (e, btn) {
      var container = btn.closest('[data-metro-tabs]');
      if (!container) return;
      e.preventDefault();
      applyTabs(container, btn.getAttribute('data-tab'));
    });
  }

  /* ------------------------------------------------------------ accordion */

  function closeAccordionItem(item) {
    item.setAttribute('data-open', 'false');
    var t = $('[data-accordion-trigger]', item);
    var p = $('[data-accordion-panel]', item);
    var plus = $('[data-accordion-plus]', item);
    var minus = $('[data-accordion-minus]', item);
    var badge = $('[data-accordion-badge]', item);
    if (t) t.setAttribute('aria-expanded', 'false');
    if (p) p.hidden = true;
    if (plus) plus.hidden = false;
    if (minus) minus.hidden = true;
    if (badge) {
      setClasses(badge, badge.getAttribute('data-active-class'), false);
      setClasses(badge, badge.getAttribute('data-idle-class'), true);
    }
  }

  function bindAccordion() {
    on('click', '[data-accordion-trigger]', function (e, btn) {
      e.preventDefault();
      var item = btn.closest('[data-accordion-item]');
      var group = btn.closest('[data-accordion]');
      if (!item || !group) return;
      var next = item.getAttribute('data-open') !== 'true';

      if (group.getAttribute('data-accordion') !== 'multiple') {
        $$('[data-accordion-item]', group).forEach(function (other) {
          if (other !== item) closeAccordionItem(other);
        });
      }

      item.setAttribute('data-open', next ? 'true' : 'false');
      btn.setAttribute('aria-expanded', next ? 'true' : 'false');
      var panel = $('[data-accordion-panel]', item);
      var plus = $('[data-accordion-plus]', item);
      var minus = $('[data-accordion-minus]', item);
      var badge = $('[data-accordion-badge]', item);
      if (panel) panel.hidden = !next;
      if (plus) plus.hidden = next;
      if (minus) minus.hidden = !next;
      if (badge) {
        setClasses(badge, badge.getAttribute('data-active-class'), next);
        setClasses(badge, badge.getAttribute('data-idle-class'), !next);
      }
    });
  }

  /* ----------------------------------------------------------------- quiz */

  function initQuiz() {
    each('[data-metro-quiz]', 'Quiz', function (root) {
      var steps = $$('[data-quiz-step]', root);
      var result = $('[data-quiz-result]', root);
      var bar = $('[data-quiz-progress]', root);
      var total = steps.length;
      var step = 0;
      var score = { city: 0, pro: 0 };

      function render() {
        steps.forEach(function (s, i) {
          s.hidden = i !== step || step >= total;
        });
        if (result) result.hidden = step < total;
        if (bar) bar.style.width = (Math.min(step, total) / total) * 100 + '%';

        if (step >= total && result) {
          var winner = score.pro > score.city ? 'pro' : 'city';
          $$('[data-quiz-winner]', result).forEach(function (el) {
            el.hidden = el.getAttribute('data-quiz-winner') !== winner;
          });
        }
      }

      root.addEventListener('click', function (event) {
        var answer = event.target.closest('[data-quiz-answer]');
        if (answer && root.contains(answer)) {
          event.preventDefault();
          score.city += parseInt(answer.getAttribute('data-score-city') || '0', 10);
          score.pro += parseInt(answer.getAttribute('data-score-pro') || '0', 10);
          step += 1;
          render();
          if (step >= total) {
            root.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }

        var reset = event.target.closest('[data-quiz-reset]');
        if (reset && root.contains(reset)) {
          event.preventDefault();
          step = 0;
          score = { city: 0, pro: 0 };
          render();
        }
      });

      render();
    });
  }

  /* -------------------------------------------------------------- builder */
  /* "Build your ride": model x colour picks the render, price and buy link.  */

  function initBuilder() {
    each('[data-metro-builder]', 'Builder', function (root) {
      function render() {
        var model = root.getAttribute('data-model');
        var color = root.getAttribute('data-color');

        $$('[data-builder-model]', root).forEach(function (btn) {
          var active = btn.getAttribute('data-builder-model') === model;
          btn.setAttribute('aria-pressed', active ? 'true' : 'false');
          setClasses(btn, btn.getAttribute('data-active-class'), active);
          setClasses(btn, btn.getAttribute('data-idle-class'), !active);
        });
        $$('[data-builder-color]', root).forEach(function (btn) {
          var active = btn.getAttribute('data-builder-color') === color;
          btn.setAttribute('aria-pressed', active ? 'true' : 'false');
          setClasses(btn, btn.getAttribute('data-active-class'), active);
          setClasses(btn, btn.getAttribute('data-idle-class'), !active);
        });

        // Panels keyed by "model:colour" or by "model" alone.
        $$('[data-builder-for]', root).forEach(function (el) {
          var key = el.getAttribute('data-builder-for');
          el.hidden = key !== model + ':' + color && key !== model;
        });
      }

      root.addEventListener('click', function (event) {
        var modelBtn = event.target.closest('[data-builder-model]');
        if (modelBtn && root.contains(modelBtn)) {
          event.preventDefault();
          root.setAttribute('data-model', modelBtn.getAttribute('data-builder-model'));
          render();
          return;
        }
        var colorBtn = event.target.closest('[data-builder-color]');
        if (colorBtn && root.contains(colorBtn)) {
          event.preventDefault();
          root.setAttribute('data-color', colorBtn.getAttribute('data-builder-color'));
          render();
        }
      });

      render();
    });
  }

  /* ----------------------------------------------------------- sticky bar */

  function initStickyBars() {
    each('[data-sticky-bar]', 'Sticky', function (bar) {
      var threshold = parseInt(bar.getAttribute('data-sticky-bar') || '700', 10);
      var hiddenClass = bar.getAttribute('data-hidden-class') || 'translate-y-16';
      function onScroll() {
        var show = window.scrollY > threshold;
        bar.classList.toggle('translate-y-0', show);
        bar.classList.toggle('opacity-100', show);
        bar.classList.toggle(hiddenClass, !show);
        bar.classList.toggle('opacity-0', !show);
      }
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    });
  }

  /* ------------------------------------------------------- drag-to-rotate */

  function initDragStage() {
    each('[data-drag-stage]', 'Drag', function (stage) {
      var img = $('[data-drag-target]', stage);
      if (!img) return;
      var rot = 0;
      var drag = null;

      stage.addEventListener('pointerdown', function (e) {
        drag = { x: e.clientX, base: rot };
        stage.setPointerCapture(e.pointerId);
      });
      stage.addEventListener('pointermove', function (e) {
        if (!drag) return;
        rot = drag.base + (e.clientX - drag.x) * 0.4;
        img.style.transform = 'perspective(1200px) rotateY(' + rot + 'deg)';
      });
      function up() {
        drag = null;
      }
      stage.addEventListener('pointerup', up);
      stage.addEventListener('pointercancel', up);
    });
  }

  /* ---------------------------------------------------------- model stage */
  /* Ports ModelStage.tsx: <model-viewer> is only fetched when a stage exists. */

  var modelViewerLoaded = false;
  function ensureModelViewer() {
    if (modelViewerLoaded) return;
    if (!$('[data-model-stage] model-viewer')) return;
    modelViewerLoaded = true;
    var s = document.createElement('script');
    s.type = 'module';
    s.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.1.0/model-viewer.min.js';
    document.head.appendChild(s);
  }

  /* ------------------------------------------------------------- variants */

  function initVariantPickers() {
    each('[data-variant-picker]', 'Variants', function (root) {
      var data = $('[data-variant-json]', root);
      if (!data) return;
      var variants;
      try {
        variants = JSON.parse(data.textContent || '[]');
      } catch (err) {
        return;
      }
      if (!variants.length) return;

      function select(id) {
        var variant =
          variants.filter(function (v) {
            return String(v.id) === String(id);
          })[0] || variants[0];

        root.setAttribute('data-variant-id', variant.id);
        var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

        $$('[data-variant-option]', root).forEach(function (btn) {
          var active = String(btn.getAttribute('data-variant-option')) === String(variant.id);
          btn.setAttribute('aria-pressed', active ? 'true' : 'false');
          setClasses(btn, btn.getAttribute('data-active-class'), active);
          setClasses(btn, btn.getAttribute('data-idle-class'), !active);
        });
        $$('[data-variant-price]', root).forEach(function (el) {
          el.textContent = formatMoney(variant.price);
        });
        $$('[data-variant-compare]', root).forEach(function (el) {
          if (onSale) el.textContent = formatMoney(variant.compare_at_price);
          el.hidden = !onSale;
        });
        $$('[data-variant-savings]', root).forEach(function (el) {
          if (onSale) {
            el.textContent =
              Math.round(
                ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
              ) + '% OFF';
          }
          el.hidden = !onSale;
        });
        $$('[data-variant-input]', root).forEach(function (input) {
          input.value = variant.id;
        });
        $$('[data-variant-label]', root).forEach(function (el) {
          el.textContent = variant.title;
        });
        $$('[data-variant-media]', root).forEach(function (el) {
          el.hidden = String(el.getAttribute('data-variant-media')) !== String(variant.id);
        });
        $$('[data-variant-submit]', root).forEach(function (btn) {
          btn.disabled = !variant.available;
          var labelEl = $('[data-variant-submit-label]', btn) || btn;
          labelEl.textContent = variant.available
            ? btn.getAttribute('data-label-available') || 'Add to cart'
            : strings.soldOut || 'Sold out';
        });

        // The sticky bar lives outside the picker, so mirror the state there.
        $$('[data-sticky-bar] [data-variant-input]').forEach(function (input) {
          input.value = variant.id;
        });
        $$('[data-sticky-bar] [data-variant-price]').forEach(function (el) {
          el.textContent = formatMoney(variant.price);
        });
        $$('[data-sticky-bar] [data-variant-label]').forEach(function (el) {
          el.textContent = variant.title;
        });

        if (root.hasAttribute('data-update-url') && history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url.toString());
        }
      }

      root.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-variant-option]');
        if (!btn || !root.contains(btn)) return;
        event.preventDefault();
        select(btn.getAttribute('data-variant-option'));
      });

      // The sticky bar's swatches drive the same picker.
      $$('[data-sticky-bar] [data-variant-option]').forEach(function (btn) {
        if (btn.dataset.metroReadyStickySwatch === 'true') return;
        btn.dataset.metroReadyStickySwatch = 'true';
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          select(btn.getAttribute('data-variant-option'));
        });
      });

      select(root.getAttribute('data-variant-id') || variants[0].id);
    });
  }

  /* ----------------------------------------------------------------- cart */

  var Cart = {
    busy: false,

    refresh: function () {
      return fetch(routes.root + '?sections=cart-drawer', {
        headers: { Accept: 'application/json' }
      })
        .then(function (res) {
          return res.json();
        })
        .then(function (json) {
          var html = json['cart-drawer'];
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-cart-drawer-body]');
          var current = $('[data-cart-drawer-body]');
          if (fresh && current) current.innerHTML = fresh.innerHTML;
          var freshSummary = doc.querySelector('[data-cart-summary]');
          var summary = $('[data-cart-summary]');
          if (freshSummary && summary) summary.textContent = freshSummary.textContent;
        })
        .catch(function () {
          /* keep the existing markup on failure */
        })
        .then(function () {
          return Cart.updateCount();
        });
    },

    updateCount: function () {
      return fetch(routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (res) {
          return res.json();
        })
        .then(function (cart) {
          Theme.cartCount = cart.item_count;
          $$('[data-cart-count]').forEach(function (el) {
            el.textContent = cart.item_count;
          });
          $$('[data-cart-total]').forEach(function (el) {
            el.textContent = formatMoney(cart.total_price);
          });
          document.dispatchEvent(new CustomEvent('metro:cart:updated', { detail: cart }));
          return cart;
        })
        .catch(function () {
          return null;
        });
    },

    add: function (id, quantity, properties) {
      if (Cart.busy) return Promise.resolve(null);
      Cart.busy = true;
      var body = { items: [{ id: id, quantity: quantity || 1 }] };
      if (properties) body.items[0].properties = properties;

      return fetch(routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (res) {
          return res.json().then(function (json) {
            return { ok: res.ok, json: json };
          });
        })
        .then(function (result) {
          if (!result.ok) {
            Toast.error(
              result.json.description || result.json.message || strings.cartError
            );
            return null;
          }
          return Cart.refresh().then(function () {
            return result.json;
          });
        })
        .catch(function () {
          Toast.error(strings.cartError);
          return null;
        })
        .then(function (out) {
          Cart.busy = false;
          return out;
        });
    },

    change: function (line, quantity) {
      if (Cart.busy) return Promise.resolve();
      Cart.busy = true;
      var drawer = $('[data-cart-drawer-body]');
      if (drawer) drawer.setAttribute('aria-busy', 'true');

      return fetch(routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (res) {
          return res.json();
        })
        .then(function () {
          return Cart.refresh();
        })
        .catch(function () {
          Toast.error(strings.cartError);
        })
        .then(function () {
          if (drawer) drawer.removeAttribute('aria-busy');
          Cart.busy = false;
        });
    }
  };
  window.MetroCart = Cart;

  function bindCart() {
    on('submit', 'form[data-cart-form]', function (e, form) {
      e.preventDefault();
      var idInput = $('[name="id"]', form);
      if (!idInput || !idInput.value) return;
      var qtyInput = $('[name="quantity"]', form);
      var submit = $('[type="submit"]', form);
      if (submit) {
        submit.setAttribute('data-loading', 'true');
        submit.disabled = true;
      }

      Cart.add(idInput.value, qtyInput ? Number(qtyInput.value) : 1).then(function (result) {
        if (submit) {
          submit.removeAttribute('data-loading');
          submit.disabled = false;
        }
        if (!result) return;
        var name = form.getAttribute('data-product-title') || 'Item';
        Toast.success(name + ' ' + (strings.addedToCart || 'added to cart'));
        if (form.getAttribute('data-open-drawer') !== 'false') openDrawer('cart');
      });
    });

    on('click', '[data-cart-change]', function (e, btn) {
      e.preventDefault();
      Cart.change(btn.getAttribute('data-line'), Number(btn.getAttribute('data-cart-change')));
    });

    on('click', '[data-cart-remove]', function (e, btn) {
      e.preventDefault();
      Cart.change(btn.getAttribute('data-line'), 0);
    });

    // "Coming soon" accessories keep the React toast copy.
    on('click', '[data-notify-only]', function (e, btn) {
      e.preventDefault();
      Toast.info(
        btn.getAttribute('data-notify-message') ||
          'Coming soon. We will let you know when it drops.'
      );
    });
  }

  /* ------------------------------------------------ product recommendations */

  function initRecommendations() {
    each('[data-recommendations-url]', 'Recs', function (el) {
      var section = el.closest('.shopify-section') || el.parentNode;
      fetch(el.getAttribute('data-recommendations-url'))
        .then(function (res) {
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('.shopify-section') || doc.body.firstElementChild;
          if (fresh && section) {
            section.innerHTML = fresh.innerHTML;
            initElements();
          }
        })
        .catch(function () {
          /* leave the placeholder empty */
        });
    });
  }

  /* --------------------------------------------------------- contact form */

  function initContactForm() {
    each('[data-metro-contact]', 'Contact', function (form) {
      var wrap = form.closest('[data-contact-wrap]') || document;
      var sentPanel = $('[data-contact-sent]', wrap);

      form.addEventListener('submit', function (e) {
        if (form.getAttribute('data-mode') !== 'mailto') return; // Shopify posts natively
        e.preventDefault();
        var data = new FormData(form);
        var name = (data.get('contact[name]') || data.get('name') || '').toString().trim();
        var email = (data.get('contact[email]') || data.get('email') || '').toString().trim();
        var phone = (data.get('contact[phone]') || data.get('phone') || '').toString().trim();
        var message = (data.get('contact[body]') || data.get('message') || '').toString().trim();

        if (!name) return Toast.error('Name is required');
        if (name.length > 100) return Toast.error('Name is too long');
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return Toast.error('Enter a valid email');
        if (!message) return Toast.error('Message is required');
        if (message.length > 2000) return Toast.error('Message is too long');

        var to = form.getAttribute('data-mailto');
        var subject = encodeURIComponent('[METRO] Message from ' + name);
        var body = encodeURIComponent(
          'Name: ' + name + '\nEmail: ' + email + '\nPhone: ' + phone + '\n\n' + message
        );
        window.location.href = 'mailto:' + to + '?subject=' + subject + '&body=' + body;
        setTimeout(function () {
          if (sentPanel) {
            sentPanel.hidden = false;
            form.hidden = true;
          }
          Toast.success('Message ready to send in your email app');
        }, 400);
      });
    });
  }

  function bindContactReset() {
    on('click', '[data-contact-reset]', function (e, btn) {
      e.preventDefault();
      var wrap = btn.closest('[data-contact-wrap]');
      if (!wrap) return;
      var sent = $('[data-contact-sent]', wrap);
      var form = $('[data-metro-contact]', wrap);
      if (sent) sent.hidden = true;
      if (form) {
        form.hidden = false;
        form.reset();
      }
    });
  }

  /* --------------------------------------------------------------- search */

  function initSearch() {
    each('[data-search-input]', 'Search', function (input) {
      var results = $('[data-search-results]');
      var run = debounce(function () {
        var q = input.value.trim();
        if (!results) return;
        if (q.length < 2) {
          results.innerHTML = '';
          return;
        }
        fetch(
          routes.predictiveSearch +
            '?q=' +
            encodeURIComponent(q) +
            '&resources[type]=product,page,collection&resources[limit]=6&section_id=predictive-search'
        )
          .then(function (res) {
            return res.text();
          })
          .then(function (html) {
            results.innerHTML = html;
          })
          .catch(function () {
            results.innerHTML = '';
          });
      }, 250);
      input.addEventListener('input', run);
    });
  }

  /* ------------------------------------------------------- smooth anchors */

  function bindSmoothAnchors() {
    on('click', 'a[href^="#"]', function (e, a) {
      var id = a.getAttribute('href');
      if (!id || id === '#') return;
      var target;
      try {
        target = document.querySelector(id);
      } catch (err) {
        return;
      }
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      history.replaceState(null, '', id);
    });
  }

  /* ------------------------------------------------------------------ init */

  function initElements() {
    initHeader();
    initCountdown();
    initTabs();
    initQuiz();
    initBuilder();
    initStickyBars();
    initDragStage();
    initVariantPickers();
    initContactForm();
    initSearch();
    initRecommendations();
    ensureModelViewer();
  }

  function bindDelegates() {
    bindDrawers();
    bindTabs();
    bindAccordion();
    bindCart();
    bindContactReset();
    bindSmoothAnchors();
  }

  function boot() {
    bindDelegates();
    initElements();
    Cart.updateCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: only re-run the per-element setup, never the delegates.
  document.addEventListener('shopify:section:load', initElements);
  document.addEventListener('shopify:section:select', initElements);
})();
